import React, { useState, useEffect } from 'react';

import { Grid, DonutChart, LineChart } from './TremorCompat';

import { Button, ButtonToolbar, FlexboxGrid, List, Stack } from 'rsuite';

import {
  MdOutlineAddCircle as IconMore,
  MdOutlineRemoveCircle as IconLess,
  MdOutlineSync as IconRefresh,
} from 'react-icons/md';

import { APP_CONFIG, controller_t, modelTypeDetails, modelTypeDetails_no_SWC } from '../constants';

import { COLOURS } from '../styles';
import { defaultCardStyle, headerCardStyle, progressCardStyle } from '../styles';

import ResizeObserver from 'resize-observer-polyfill';
import { LoadingView } from './DisplayItems';

const StatCard = ({ title, data, colors, showList, totals }) => {
  const [sum, setSum] = useState(0);

  useEffect(() => {
    if (totals !== undefined && totals !== 0) {
      setSum(totals);
    } else {
      const temp = data.reduce((acc, obj) => acc + obj.value, 0);
      setSum(temp);
    }
  }, [data, totals]);

  return (
    sum !== 0 && (
      <Stack alignItems="stretch" justifyContent="flex-start" direction="column" spacing={16} style={defaultCardStyle}>
        <span style={{ fontSize: '1.1em', fontWeight: 'bold' }}>{title + ' (' + sum + ')'}</span>
        <DonutChart
          data={data}
          category="value"
          index="name"
          colors={colors}
          showAnimation={false}
          showLabel={false}
          variant="pie"
          height={showList ? 'h-56' : 'h-80'}
        />
        {showList === true && (
          <List hover size="sm">
            {data.map((item) => (
              <List.Item key={item.name}>
                <FlexboxGrid align="middle" justify="space-between" style={{ fontSize: '0.9em' }}>
                  <FlexboxGrid.Item>{item.name + ' (' + item.value + ')'}</FlexboxGrid.Item>
                  <FlexboxGrid.Item>{((item.value * 100) / sum).toFixed(1) + '%'}</FlexboxGrid.Item>
                </FlexboxGrid>
              </List.Item>
            ))}
          </List>
        )}
      </Stack>
    )
  );
};

// Owner-confirmed 2026-07-16: Reclaim must never appear in Stats (see
// useDevices.js's stats builder) — modelTypeDetails/_no_SWC still list it
// (kept for completeness elsewhere), so it must be filtered out here too,
// or it shows up as an always-empty legend category on these charts.
// Pure functions (no closure over props/state) — hoisted out of StatsView
// and exported so they're independently testable.
export const buildCategories = (dataToMap) => {
  var categoriesBuilt = dataToMap
    .filter((item) => item.value !== controller_t.RECLAIM)
    .map((item) => item.label.toLowerCase());
  categoriesBuilt.push('Total');
  // categoriesBuilt.push( "Running Total");
  return categoriesBuilt;
};

export const buildColours = (dataToMap) => {
  var coloursBuilt = dataToMap.filter((item) => item.value !== controller_t.RECLAIM).map((item) => item.colour);
  coloursBuilt.push(COLOURS.apiAccessOff);
  // coloursBuilt.push( COLOURS.apiAccessControl);
  return coloursBuilt;
};

export const StatsView = (props) => {
  if (!window.ResizeObserver) {
    window.ResizeObserver = ResizeObserver;
  }

  return (
    <Stack alignItems="stretch" justifyContent="flex-start" direction="column" style={{ marginTop: 8 }}>
      <Stack alignItems="center" justifyContent="space-between" spacing={8} style={headerCardStyle}>
        <Stack.Item>
          <span
            style={{
              color: COLOURS.primary,
              fontSize: 16,
              marginLeft: 8,
            }}
          >
            {props.loading ? 'Loading ' + props.progress + '%' : props.stats.devicesTotal + ' Controllers'}
          </span>
        </Stack.Item>
        <Stack.Item alignSelf="flex-end">
          <ButtonToolbar>
            {props.loading === false && (
              <Button
                appearance="primary"
                color="blue"
                size="sm"
                endIcon={props.showList ? <IconLess /> : <IconMore />}
                onClick={props.onShowListChanged}
              >
                {props.showList ? 'Less' : 'More'}
              </Button>
            )}
            <Button
              appearance="primary"
              color="blue"
              size="sm"
              endIcon={<IconRefresh />}
              loading={props.loading}
              onClick={props.onRefresh}
            >
              Refresh
            </Button>
          </ButtonToolbar>
        </Stack.Item>
      </Stack>
      {props.loading === true ? (
        <LoadingView progress={props.progress} />
      ) : (
        <Stack.Item>
          <Grid numItemsSm={1} numItemsMd={2} numItemsLg={3} className="gap-4  mt-2">
            <StatCard
              title="Production"
              data={[
                { name: 'Registered', value: props.stats.devicesRegistered },
                { name: 'Not Registered', value: props.stats.devicesNotRegistered },
                { name: 'Un Registered', value: props.stats.devicesUnRegistered },
                { name: 'In Production', value: props.stats.devicesinProduction },
              ]}
              colors={[COLOURS.default, COLOURS.unknown, COLOURS.other]}
              showList={props.showList}
            />

            <StatCard
              title="Model Type"
              data={props.stats.modelType}
              colors={props.stats.modelType.map((item) => item.colour)}
              showList={props.showList}
            />

            <StatCard
              title="Controller Status"
              data={[
                { name: 'Good', value: props.stats.devicesStatusGood },
                { name: 'Fault', value: props.stats.devicesStatusFault },
                { name: 'Offline', value: props.stats.devicesStatusOffline },
                // { name: "Unknown", value: props.stats.devicesStatusUnknown }
              ]}
              colors={[COLOURS.good, COLOURS.error, COLOURS.warning, COLOURS.unknown]}
              showList={props.showList}
            />
            <StatCard
              title="Faults"
              data={props.stats.faults}
              colors={props.stats.faults.map((item) => item.colour)}
              showList={props.showList}
              totals={0}
            />

            <StatCard
              title="Versions"
              data={props.stats.versions}
              colors={props.stats.versions.map((item) => item.colour)}
              showList={props.showList}
            />
          </Grid>

          {APP_CONFIG.SWC_ENABLED ? (
            <LineChart
              className="h-80"
              data={props.stats.createdMonth}
              index="name"
              categories={buildCategories(modelTypeDetails)}
              colors={buildColours(modelTypeDetails)}
              // valueFormatter={dataFormatter}
              yAxisWidth={60}
              showLegend={true}
            />
          ) : (
            <LineChart
              className="h-80"
              data={props.stats.createdMonth}
              index="name"
              categories={buildCategories(modelTypeDetails_no_SWC)}
              colors={buildColours(modelTypeDetails_no_SWC)}
              // valueFormatter={dataFormatter}
              yAxisWidth={60}
              showLegend={true}
            />
          )}

          {APP_CONFIG.SWC_ENABLED ? (
            <LineChart
              className="h-80"
              data={props.stats.runningTotals}
              index="name"
              categories={buildCategories(modelTypeDetails)}
              colors={buildColours(modelTypeDetails)}
              // valueFormatter={dataFormatter}
              yAxisWidth={60}
              showLegend={true}
            />
          ) : (
            <LineChart
              className="h-80"
              data={props.stats.runningTotals}
              index="name"
              categories={buildCategories(modelTypeDetails_no_SWC)}
              colors={buildColours(modelTypeDetails_no_SWC)}
              // valueFormatter={dataFormatter}
              yAxisWidth={60}
              showLegend={true}
            />
          )}

          <LineChart
            className="h-80"
            data={props.stats.createdMonth}
            index="name"
            categories={['Running Total']}
            colors={[COLOURS.apiAccessControl]}
            yAxisWidth={60}
            showLegend={true}
          />
        </Stack.Item>
      )}
    </Stack>
  );
};
